import * as d3 from 'd3';

export type EmdPeriod = 'D' | 'W' | 'M' | 'Q' | 'Y';

/** Axis tick formatter per reporting bucket. */
export function emdDateTickFormat(period: EmdPeriod): (d: Date | any) => string {
  switch (period) {
    case 'D': return d3.timeFormat('%d %b %y');
    case 'W': return d3.timeFormat('%d %b %y');
    case 'M': return d3.timeFormat('%b %Y');
    case 'Q': return d3.timeFormat('%b %Y');
    case 'Y': return d3.timeFormat('%Y');
  }
}

/**
 * Legend labels become DOM ids in these charts. A label like `SALES ($)`
 * produces an invalid selector that throws and aborts the whole draw, so every
 * id passes through here first.
 */
export function emdSafeId(label: string): string {
  return label.replace(/[^a-zA-Z0-9_-]/g, '');
}

let measureCanvas: HTMLCanvasElement | null = null;

/** Text width without touching the DOM, for laying out legends. */
export function measureTextWidth(text: string, fontSizePx: number, weight = 600): number {
  measureCanvas ??= document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) { return text.length * fontSizePx * 0.6; }
  ctx.font = `${weight} ${fontSizePx}px Poppins, sans-serif`;
  return ctx.measureText(text).width;
}

export function emdFormatNumber(value: number): string {
  if (!isFinite(value)) { return '—'; }
  const abs = Math.abs(value);
  if (abs >= 1_000_000) { return `${(value / 1_000_000).toFixed(1)}M`; }
  if (abs >= 1_000) { return `${(value / 1_000).toFixed(1)}K`; }
  return value.toLocaleString('en-US');
}

/**
 * Height a chart should draw at: its design height normally, but when its card
 * is fullscreened, the space from the chart's top edge to the bottom of the
 * screen (the original icon-chart sets `chartHeight = outerHeight - 60` per
 * chart on fullscreen; this is the shared equivalent for the EMD clones).
 */
export function emdFullscreenHeight(el: HTMLElement, fallback: number, bottomPad = 24): number {
  const fs = document.fullscreenElement as HTMLElement | null;
  if (!fs || !fs.contains(el)) { return fallback; }
  const top = el.getBoundingClientRect().top - fs.getBoundingClientRect().top;
  return Math.max(300, fs.clientHeight - top - bottomPad);
}

/**
 * Break a <text> element's content into tspans no wider than `width`,
 * stacking downward — port of the original combo-bar-card's `wrap`, used for
 * the axis captions that sit in the narrow gutters above the y axes.
 */
export function emdWrapText(
  selection: d3.Selection<SVGTextElement, unknown, null, undefined>,
  width: number
): void {
  selection.each(function () {
    const text = d3.select(this);
    const words = text.text().split(/\s+/).reverse();
    const lineHeight = 10;
    let line: string[] = [];
    let lineNumber = 0;
    let word: string | undefined;
    let tspan = text.text(null).append('tspan').attr('x', 0).attr('y', 0).attr('dy', '0px');

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(' '));
      if ((tspan.node()?.getComputedTextLength() ?? 0) > width && line.length > 1) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        lineNumber += 1;
        tspan = text
          .append('tspan')
          .attr('x', 0)
          .attr('y', 0)
          .attr('dy', `${lineNumber * lineHeight}px`)
          .text(word);
      }
    }
  });
}

export interface EmdZoomBrushOptions {
  /** Group the brush is drawn into, already translated into position. */
  group: d3.Selection<SVGGElement, unknown, null, undefined>;
  width: number;
  height: number;
  xScaleAll: d3.ScaleTime<number, number>;
  /** Every x value in the full dataset, ascending. */
  dates: Date[];
  /** Fires with the visible domain; also fires once with the initial window. */
  onBrush: (domain: [Date, Date]) => void;
  /** Draws the mini preview marks inside the strip. */
  drawPreview: (
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    height: number
  ) => void;
}

/**
 * The range-selector strip below the trend charts: a miniature of the full
 * series with a draggable window over it.
 *
 * Note this is the `brushX` preview strip, not the shared combo-bar chart's
 * `createRangeSlider` — that one is a vertical y-axis clamp gated behind config
 * EMD never sets, and is deliberately not reproduced.
 */
export function createZoomBrush(opts: EmdZoomBrushOptions): void {
  const { group, width, height, xScaleAll, dates, onBrush, drawPreview } = opts;

  group.selectAll('*').remove();

  const preview = group.append('g').attr('class', 'emd-brush-preview');
  drawPreview(preview, xScaleAll, height);

  const clampToData = (domain: [Date, Date]): [Date, Date] => {
    const inWindow = dates.filter((d) => d >= domain[0] && d <= domain[1]);
    // A window narrower than two points has nothing to plot; widen it back.
    if (inWindow.length >= 2) { return domain; }
    const last = dates[dates.length - 1];
    const fallbackStart = dates[Math.max(0, dates.length - 2)];
    return [fallbackStart, last];
  };

  const brush = d3
    .brushX()
    .extent([[0, 0], [width, height]])
    .on('brush end', (event: any) => {
      if (!event.selection) { return; }
      const domain = (event.selection as [number, number]).map((v) =>
        xScaleAll.invert(v)
      ) as [Date, Date];
      onBrush(clampToData(domain));
      styleHandles();
    });

  const brushGroup = group.append('g').attr('class', 'emd-brush').call(brush as any);

  // Original handles: 24px white pills centred in the strip, each with two grey
  // grip ticks. d3 resets y/height on every move, so re-apply after each brush.
  const HANDLE_HEIGHT = 24;
  const styleHandles = (): void => {
    brushGroup.selectAll('.emd-brush-grip').remove();
    brushGroup
      .selectAll<SVGRectElement, unknown>('.handle')
      .attr('y', (height - HANDLE_HEIGHT) / 2)
      .attr('height', HANDLE_HEIGHT);

    brushGroup.selectAll<SVGRectElement, unknown>('.handle').each(function () {
      const handleX = +(d3.select(this).attr('x') ?? 0);
      [3, 7].forEach((dx) => {
        brushGroup
          .append('line')
          .attr('class', 'emd-brush-grip')
          .attr('x1', handleX + dx)
          .attr('x2', handleX + dx)
          .attr('y1', (height - 12) / 2)
          .attr('y2', (height + 12) / 2)
          .attr('stroke', '#8A98AB')
          .attr('stroke-width', 1)
          .attr('pointer-events', 'none');
      });
    });
  };

  // Open on a readable window rather than the full extract, matching how the
  // app's own trend charts start.
  const visibleCount = Math.max(2, Math.min(Math.floor(width / 25), dates.length));
  const startDate = dates[Math.max(0, dates.length - visibleCount)];
  const endDate = dates[dates.length - 1];

  if (startDate && endDate) {
    brushGroup.call(brush.move as any, [xScaleAll(startDate), xScaleAll(endDate)]);
  }
  styleHandles();

  // Double-click anywhere on the strip restores the full range.
  group.on('dblclick', () => {
    brushGroup.call(brush.move as any, [0, width]);
  });
}
